import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';

import { createGlobalStyle } from 'styled-components';

// Optional react-grab integration (disabled by default).
// NOTE: react-grab currently triggers "Illegal invocation" in the appointment form
// due to its global addEventListener patch. Keep it opt-in via env flag.
const enableReactGrab =
  (process.env.REACT_APP_ENABLE_REACT_GRAB === 'true') ||
  ((import.meta as any)?.env?.VITE_ENABLE_REACT_GRAB === 'true');

if (process.env.NODE_ENV === 'development' && enableReactGrab) {
  import('react-grab').catch((err) => {
    console.warn('react-grab failed to load, continuing without it', err);
  });
}

// Lazy load AdminApp to prevent blocking main app if it has errors
const AdminApp = lazy(() => import('./admin/AdminApp'));

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
  }

  body {
    min-height: 100vh;
    overflow-x: hidden;
    background: transparent;
    position: relative;
    transition: background 0.3s ease;
  }
  
  .dark body {
    background: transparent;
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
`;

// Global CSS for high-quality device images
const globalStyles = `
  .high-quality-device-image {
    image-rendering: -webkit-optimize-contrast !important;
    filter: contrast(1.2) saturate(1.15) brightness(1.08) !important;
    transition: all 0.3s ease !important;
  }
  
  .high-quality-device-image:hover {
    filter: contrast(1.25) saturate(1.25) brightness(1.12) !important;
    transform: scale(1.02) !important;
  }
  
  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    .high-quality-device-image {
      image-rendering: auto !important;
      filter: contrast(1.18) saturate(1.2) brightness(1.1) !important;
    }
  }
`;

// Inject styles into head
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = globalStyles;
document.head.appendChild(styleSheet);

// Check if root element exists
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

const root = ReactDOM.createRoot(rootElement);

// Add error logging
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
});

try {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <GlobalStyle />
        <BrowserRouter>
          <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Laden...</div>}>
            <Routes>
              <Route path="/admin/*" element={<AdminApp />} />
              <Route path="*" element={<App />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );
} catch (error) {
  console.error('Error rendering app:', error);
  rootElement.innerHTML = `
    <div style="padding: 2rem; text-align: center; font-family: system-ui;">
      <h1>Fout bij het laden</h1>
      <p>${error instanceof Error ? error.message : 'Onbekende fout'}</p>
      <button onclick="window.location.reload()">Vernieuwen</button>
    </div>
  `;
}
